import { memo, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Breadcrumb,
  Button,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components';
import ToolItem from '@/components/Editor/toolItem';
import type { Editor } from '@/components/Editor/types';

import {
  ChildrenPage,
  ConnectionStatus,
  DocsItem,
  getChildren,
  getConnection,
  getLink,
  getRoot,
} from './services';
import './docs.scss';

type Crumb = {
  id: string;
  name: string;
};

type PageError = {
  code: string;
  retryAfter?: number;
};

const errorFrom = (error: any): PageError => ({
  code: error?.data?.code || 'unavailable',
  retryAfter: error?.data?.retryAfter,
});

const DocsPicker = () => {
  const { t } = useTranslation('plugin');
  const [available, setAvailable] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Crumb[]>([]);
  const [items, setItems] = useState<DocsItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadingFolder, setLoadingFolder] = useState(false);
  const [inserting, setInserting] = useState<string | null>(null);
  const [error, setError] = useState<PageError | null>(null);

  useEffect(() => {
    let active = true;
    getConnection(false)
      .then((value) => {
        if (!active) return;
        setAvailable(
          Boolean(
            value.available &&
              value.status === 'connected' &&
              value.configurationValid &&
              value.markdownLinkEnabled,
          ),
        );
      })
      .catch(() => {
        if (active) setAvailable(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const loadFolder = useCallback(
    async (itemId: string, append = false, cursor?: string) => {
      setLoadingFolder(true);
      setError(null);
      try {
        const page: ChildrenPage = await getChildren(itemId, cursor);
        setItems((current) => (append ? [...current, ...page.items] : page.items));
        setNextCursor(page.nextCursor);
      } catch (requestError) {
        setError(errorFrom(requestError));
      } finally {
        setLoadingFolder(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!visible) return;
    let active = true;
    setLoading(true);
    setError(null);
    getConnection(true)
      .then(async (value) => {
        if (!active) return;
        setConnection(value);
        if (
          !value.available ||
          value.status !== 'connected' ||
          !value.configurationValid ||
          !value.markdownLinkEnabled
        ) {
          setAvailable(false);
          return;
        }
        const root = await getRoot();
        if (!active) return;
        setBreadcrumbs([{ id: root.id, name: root.displayLabel }]);
        await loadFolder(root.id);
      })
      .catch((requestError) => {
        if (active) setError(errorFrom(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [visible, loadFolder]);

  const openPicker = (currentEditor: Editor) => {
    setEditor(currentEditor);
    setVisible(true);
  };

  const openFolder = (item: DocsItem) => {
    setBreadcrumbs((current) => [...current, { id: item.id, name: item.name }]);
    setItems([]);
    loadFolder(item.id);
  };

  const openBreadcrumb = (index: number) => {
    const crumb = breadcrumbs[index];
    setBreadcrumbs((current) => current.slice(0, index + 1));
    setItems([]);
    loadFolder(crumb.id);
  };

  const insertDocument = async (item: DocsItem) => {
    if (!editor) return;
    setInserting(item.id);
    setError(null);
    try {
      const result = await getLink(item.id);
      if (!result.markdown) {
        throw new Error('Markdown links are unavailable');
      }
      editor.replaceSelection(result.markdown);
      setVisible(false);
      editor.focus();
    } catch (requestError) {
      setError(errorFrom(requestError));
    } finally {
      setInserting(null);
    }
  };

  const errorMessage = (value: PageError) => {
    const key: Record<string, string> = {
      access_denied: 'access_denied',
      deleted: 'deleted',
      throttled: 'throttled',
    };
    return t(`docs_graph.${key[value.code] || 'generic_error'}`);
  };

  if (!available) return null;

  return (
    <>
      <ToolItem
        label="folder-fill"
        tip={t('docs_graph.insert_document')}
        onClick={openPicker}
      />
      <Modal
        show={visible}
        onHide={() => setVisible(false)}
        onExited={() => editor?.focus()}
        size="lg"
        fullscreen="sm-down"
        scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{t('docs_graph.select_document')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {errorMessage(error)}
              {error.retryAfter ? ` (${error.retryAfter}s)` : ''}
            </Alert>
          )}
          {loading ? (
            <div className="docs-graph-loading" aria-live="polite">
              <Spinner animation="border" size="sm" className="me-2" />
              {t('docs_graph.loading')}
            </div>
          ) : connection && (
            <>
              <Breadcrumb aria-label={t('docs_graph.select_document')}>
                {breadcrumbs.map((crumb, index) => (
                  <Breadcrumb.Item
                    key={crumb.id}
                    active={index === breadcrumbs.length - 1}
                    onClick={() => {
                      if (index !== breadcrumbs.length - 1) openBreadcrumb(index);
                    }}>
                    {crumb.name}
                  </Breadcrumb.Item>
                ))}
              </Breadcrumb>
              <div className="table-responsive">
                <Table hover className="docs-graph-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>{t('docs_graph.name')}</th>
                      <th>{t('docs_graph.type')}</th>
                      <th className="text-end" aria-label={t('docs_graph.select_document')} />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <Button
                            variant="link"
                            className="docs-graph-item"
                            onClick={() =>
                              item.kind === 'folder'
                                ? openFolder(item)
                                : insertDocument(item)
                            }>
                            <Icon
                              name={item.kind === 'folder' ? 'folder-fill' : 'file-earmark-fill'}
                              className="me-2"
                            />
                            {item.name}
                          </Button>
                        </td>
                        <td>{t(`docs_graph.${item.kind}`)}</td>
                        <td className="text-end">
                          {item.kind === 'file' && (
                            <Button
                              variant="primary"
                              size="sm"
                              disabled={inserting === item.id}
                              onClick={() => insertDocument(item)}>
                              {inserting === item.id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                t('docs_graph.insert')
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {loadingFolder && (
                <div className="docs-graph-loading" aria-live="polite">
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('docs_graph.loading')}
                </div>
              )}
              {!loadingFolder && items.length === 0 && !error && (
                <p className="text-secondary text-center py-4 mb-0">
                  {t('docs_graph.empty')}
                </p>
              )}
              {nextCursor && !loadingFolder && (
                <div className="text-center mt-3">
                  <Button
                    variant="outline-primary"
                    onClick={() =>
                      loadFolder(
                        breadcrumbs[breadcrumbs.length - 1].id,
                        true,
                        nextCursor,
                      )
                    }>
                    {t('docs_graph.load_more')}
                  </Button>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default memo(DocsPicker);
